import React,{createContext,useContext,useState,useEffect} from 'react';
import {useSupabase} from './SupabaseContext';
import {useAuth} from './AuthContext';
import toast from 'react-hot-toast';

const AdminContext=createContext({});

export const useAdmin=()=> {
  const context=useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider=({children})=> {
  const [isAdmin,setIsAdmin]=useState(false);
  const [isLoading,setIsLoading]=useState(true);
  const supabase=useSupabase();
  const {user}=useAuth();

  useEffect(()=> {
    const checkAdminStatus=async ()=> {
      if (!user) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      try {
        const {data,error}=await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id',user.id)
          .single();
        if (error) throw error;
        setIsAdmin(data.is_admin);
      } catch (error) {
        console.error('Error checking admin status:',error);
        setIsAdmin(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAdminStatus();
  },[user,supabase]);
  
  const getAdminStats=async ()=> {
    if (!isAdmin) {
      toast.error('Access denied: Admin privileges required.');
      return {total_users: 0, total_campaigns: 0, total_broadcasts: 0};
    }
    try {
      const {data,error}=await supabase.rpc('get_admin_stats').single();
      if (error) throw error;
      return data;
    } catch (error) {
      toast.error('Failed to fetch admin stats.');
      console.error('Error fetching stats:',error);
      return {total_users: 0, total_campaigns: 0, total_broadcasts: 0};
    }
  };

  const getAllUsers=async ()=> {
    if (!isAdmin) {
      toast.error('Access denied: Admin privileges required.');
      return [];
    }
    try {
      const {data,error}=await supabase.rpc('get_all_users');
      if (error) throw error;
      return data;
    } catch (error) {
      toast.error('Failed to fetch users.');
      console.error('Error fetching users:',error);
      return [];
    }
  };

  const sendBroadcast=async (subject,message)=> {
    if (!isAdmin) {
      toast.error('Access denied: Admin privileges required.');
      return {success: false};
    }
    try {
      const {data,error}=await supabase.rpc('send_user_broadcast',{
        subject_input: subject,
        message_input: message,
      });
      if (error) throw error;
      toast.success(`Broadcast queued for ${data[0].recipient_count} users!`);
      return data[0];
    } catch (error) {
      toast.error('Failed to send broadcast.');
      console.error('Error sending broadcast:',error);
      return {success: false,message: error.message};
    }
  };

  const getBroadcastLogs=async ()=> {
    if (!isAdmin) {
      toast.error('Access denied: Admin privileges required.');
      return [];
    }
    try {
      const {data,error}=await supabase
        .from('broadcast_logs')
        .select('*,sender_id(id,email)')
        .order('created_at',{ascending: false});
      if (error) throw error;
      return data;
    } catch (error) {
      toast.error('Failed to fetch broadcast logs.');
      console.error('Error fetching logs:',error);
      return [];
    }
  };

  const value={
    isAdmin,
    isLoading,
    getAdminStats,
    getAllUsers,
    sendBroadcast,
    getBroadcastLogs,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};